'use client';

import React, { useState, useEffect } from 'react';
import { useCategoryContext } from '../page';
import { catalogService } from '@/services/catalogService';

interface SubcategoryFilterProps {
  categorySlug: string;
  loading: boolean;
}

const SubcategoryFilter = ({ categorySlug, loading }: SubcategoryFilterProps) => {
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const { selectedSubcategory, setSelectedSubcategory, allModels } = useCategoryContext();

  useEffect(() => {
    loadSubcategories();
  }, [categorySlug]);

  const loadSubcategories = async () => {
    try {
      const category = await catalogService.getCategoryBySlug(categorySlug);
      
      // Extract unique subcategories from actual models data
      if (allModels && allModels.length > 0) {
        const uniqueSubcategories = Array.from(
          new Set(
            allModels
              .map(model => model?.subcategory)
              .filter(sub => sub && sub.trim() !== '')
          )
        ).sort();
        setSubcategories(uniqueSubcategories as string[]);
      } else if (category?.subcategories) {
        // Fallback to category metadata if models haven't loaded yet
        setSubcategories(category.subcategories);
      }
    } catch (err) {
      console.error('Error loading subcategories:', err);
    }
  };

  // Reload subcategories when models change
  useEffect(() => {
    if (allModels && allModels.length > 0) {
      const uniqueSubcategories = Array.from(
        new Set(
          allModels
            .map(model => model?.subcategory)
            .filter(sub => sub && sub.trim() !== '')
        )
      ).sort();
      setSubcategories(uniqueSubcategories as string[]);
    }
  }, [allModels]);

  if (loading || subcategories.length === 0) {
    return null;
  }

  // Calculate count for each subcategory
  const getSubcategoryCount = (subcategory: string) => {
    if (subcategory === 'all') {
      return allModels?.length || 0;
    }
    return allModels?.filter(model => model?.subcategory === subcategory).length || 0;
  };

  return (
    <section data-component-id="src/app/catalog/[category]/components/SubcategoryFilter.tsx:70:4" data-component-path="src/app/catalog/[category]/components/SubcategoryFilter.tsx" data-component-line="70" data-component-end-line="96" data-component-file="SubcategoryFilter.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-6%20bg-card%20border-b%20border-border%20sticky%20top-20%20z-30%22%7D" className="py-6 bg-card border-b border-border sticky top-20 z-30">
      <div data-component-id="src/app/catalog/[category]/components/SubcategoryFilter.tsx:71:6" data-component-path="src/app/catalog/[category]/components/SubcategoryFilter.tsx" data-component-line="71" data-component-end-line="95" data-component-file="SubcategoryFilter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
        <div data-component-id="src/app/catalog/[category]/components/SubcategoryFilter.tsx:72:8" data-component-path="src/app/catalog/[category]/components/SubcategoryFilter.tsx" data-component-line="72" data-component-end-line="94" data-component-file="SubcategoryFilter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%20overflow-x-auto%20pb-2%20scrollbar-hide%22%7D" className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
          <button data-component-id="src/app/catalog/[category]/components/SubcategoryFilter.tsx:73:10" data-component-path="src/app/catalog/[category]/components/SubcategoryFilter.tsx" data-component-line="73" data-component-end-line="80" data-component-file="SubcategoryFilter.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22All%20Models%20(%20)%22%7D"
            onClick={() => setSelectedSubcategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold whitespace-nowrap transition-all ${
              selectedSubcategory === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            All Models ({getSubcategoryCount('all')})
          </button>
          {subcategories.map((sub) => (
            <button data-component-id="src/app/catalog/[category]/components/SubcategoryFilter.tsx:82:12" data-component-path="src/app/catalog/[category]/components/SubcategoryFilter.tsx" data-component-line="82" data-component-end-line="92" data-component-file="SubcategoryFilter.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22(%20)%22%7D"
              key={sub}
              onClick={() => setSelectedSubcategory(sub)}
              className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold whitespace-nowrap transition-all ${
                selectedSubcategory === sub
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              {sub} ({getSubcategoryCount(sub)})
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SubcategoryFilter;